import { makeStyles } from "tss-react/mui";
import { Box, BoxProps, Typography } from "@mui/material";
import { forwardRef, useContext, useMemo } from "react";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import { StyledBadge } from "@/app2/pages/HomePage/Components/SectionHeader";
import type { IModuleScheduled } from "@buildwitt/shapeshift-types";
import { OverdueModules } from "@/app2/pages/TrainingFeedPage/OverdueModules";
import { LocaleContext } from "@/app2/shared/contexts/LocaleContext";
import { useTodayLearning } from "@/app2/pages/HomePage/hooks/useTodayLearning";
import type { TodayLearningModuleDto } from "@/app2/pages/HomePage/types";

interface IProps extends BoxProps {}

// Styles aligned with legacy components/trainingFeedSideMenuContent/TrainingFeedSideMenuContent
const useStyles = makeStyles()((theme) => ({
	trainingFeedSideMenuContent: {
		padding: "15px",
		position: "relative",
	},
	headerRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		marginBottom: "20px",
		marginTop: "20px",
	},
	titleHeader: {
		color: theme.palette.text.primary,
		fontSize: "16px",
		fontWeight: 600,
	},
}));

/**
 * Map TodayLearningModuleDto to IModuleScheduled for OverdueModules / TrainingFeedModuleSlim.
 */
function toModuleScheduled(dto: TodayLearningModuleDto): IModuleScheduled {
	const dueDate = dto.dueDate instanceof Date ? dto.dueDate.toISOString() : (dto.dueDate ?? "");
	return {
		...dto,
		type: dto.type,
		course: { slug: dto.courseSlug, title: dto.courseTitle },
		dueDate,
	} as unknown as IModuleScheduled;
}

export const TrainingFeedSideMenuContent = forwardRef<HTMLDivElement, IProps>(
	function TrainingFeedSideMenuContent({ className, ...props }, ref) {
		const { classes, cx } = useStyles();
		const [{locale}] = useContext(LocaleContext);
		const { t } = useSSTranslation();

		const { todayLearning } = useTodayLearning(locale);

		const modules = useMemo(
			() => (todayLearning ?? []).map(toModuleScheduled),
			[todayLearning],
		);

		return (
			<Box
				ref={ref}
				className={cx(classes.trainingFeedSideMenuContent, className)}
				data-test-id="training-feed-side-menu"
				role="complementary"
				aria-label="Training feed side menu"
				{...props}
			>
				<Box className={classes.headerRow}>
					<Typography variant="subtitle1" className={classes.titleHeader}>
						{t("home_today_learning", "Today's Learning")}
					</Typography>
					<StyledBadge badgeContent={modules.length} showZero />
				</Box>
				<OverdueModules
					slim
					modules={modules}
					data-test-id="training-feed-side-menu-overdue-modules"
				/>
			</Box>
		);
	},
);
